import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  tableCellClasses,
} from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import React from 'react';

import AnchUseCasesContentSkeleton from './anch-use-cases-content-skeleton';
import Text from '../base/text';
import ProgressWithText from '../progress-with-text';

import { DesignToken } from '~/client/components/base/core';
import { useCaseRouteUtil } from '~/client/routes';
import * as GQL from '~/graphql/generated/client';

const StyledTableCell = muiStyled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: DesignToken.surface2,
    color: DesignToken.textPrimary,
    fontSize: '14px',
    fontWeight: 600,
    padding: '16px 12px',
    borderBottom: `1px solid ${DesignToken.borderSubtle}`,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: '16px 12px',
    borderBottom: `1px solid ${DesignToken.surface2}`,
    color: DesignToken.textPrimary,
    fontStyle: 'normal',
    fontWeight: 'normal',
  },
}));

const StyledTableRow = muiStyled(TableRow)(() => ({
  '&:hover': {
    backgroundColor: DesignToken.surface2,
    cursor: 'pointer',
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

interface Props {
  organizationUnitId: number;
  customerId: number | null;
}

/**
 * Table-only content component for ANCH use cases.
 * Used inside ProjectTable's alternate tab content.
 * Does NOT include Card wrapper - that's handled by ProjectTable.
 */
export const AnchUseCasesListContent: React.FC<Props> = ({
  organizationUnitId,
}) => {
  const { data, loading, error, refetch } = GQL.useUseCasesQuery({
    variables: { organizationUnitId },
    skip: !organizationUnitId,
    fetchPolicy: 'network-only',
  });

  const { navigateTo } = useCaseRouteUtil.useHelper();

  React.useEffect(() => {
    if (organizationUnitId) {
      refetch();
    }
  }, [organizationUnitId, refetch]);

  if (loading) {
    return <AnchUseCasesContentSkeleton />;
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Text color="error">Error loading use cases</Text>
      </Box>
    );
  }

  const useCases =
    data?.useCases.map((useCase) => ({
      id: useCase.id.toString(),
      name: useCase.name,
      status: useCase.status,
      totalScreenings: useCase.totalScreenings,
      screeningProgress: useCase.screeningProgress,
    })) ?? [];

  if (useCases.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Text>No use cases available for this organization</Text>
      </Box>
    );
  }

  return (
    <Table>
      <TableHead>
        <TableRow>
          <StyledTableCell align="left">
            <Text sz="title">NAME</Text>
          </StyledTableCell>
          <StyledTableCell align="center">
            <Text sz="title">STATUS</Text>
          </StyledTableCell>
          <StyledTableCell align="center">
            <Text sz="title">SCREENINGS</Text>
          </StyledTableCell>
          <StyledTableCell align="center">
            <Text sz="title">PROGRESS</Text>
          </StyledTableCell>
          <StyledTableCell align="center">
            <Text sz="title">ACTION</Text>
          </StyledTableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {useCases.map((useCase) => (
          <StyledTableRow
            key={useCase.id}
            onClick={() =>
              navigateTo('DASHBOARD', { params: { useCaseId: useCase.id } })
            }
          >
            <StyledTableCell>
              <Text>{useCase.name}</Text>
            </StyledTableCell>
            <StyledTableCell align="center">
              {useCase.status === 'COMPLETED' ? (
                <Chip
                  label="COMPLETED"
                  color="success"
                  size="small"
                  sx={{
                    fontWeight: 400,
                    backgroundColor: DesignToken.borderSubtle,
                    color: DesignToken.textPrimary,
                  }}
                />
              ) : (
                <Chip
                  label="IN PROGRESS"
                  color="warning"
                  size="small"
                  sx={{
                    fontWeight: 400,
                    backgroundColor: DesignToken.borderSubtle,
                    color: DesignToken.textPrimary,
                  }}
                />
              )}
            </StyledTableCell>
            <StyledTableCell align="center">
              <Text>{useCase.totalScreenings}</Text>
            </StyledTableCell>
            <StyledTableCell align="center">
              <ProgressWithText value={useCase.screeningProgress} />
            </StyledTableCell>
            <StyledTableCell align="center">
              <IconButton size="small">
                <ArrowForwardIcon />
              </IconButton>
            </StyledTableCell>
          </StyledTableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default AnchUseCasesListContent;
